import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, concat, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { MenuItemType } from '../models/menu-item.type';
import { transformFeaturePathToRoute } from '../utils/feature-route.util';

export interface ApiMenuItem {
  featureName?: string;
  aliasName?: string;
  path?: string;
  featureType?: string;
  parentId?: number | null;
  displayOrder?: number;
  iconName?: string;
  
  showInMenu?: boolean;
  hasRouteParam?: boolean;
  layoutType?: string;
  accessScope?: string;
  requiresApproval?: boolean;
  isMobileVisible?: boolean;
  groupCode?: string;
  
  appType?: string;
  isActive?: boolean;
  isDeleted?: boolean;
  
  groupId?: number;
  children?: ApiMenuItem[];
  imageIcon?: string;
  routerUrl?: string;
  expanded?: boolean;
  featureId?: number;
  name?: string;
  isView?: boolean;
  level?: number;
  
  // Backend returns PascalCase
  FeatureName?: string;
  AliasName?: string;
  Path?: string;
  FeatureType?: string;
  AppType?: string;
  ParentId?: number | null;
  DisplayOrder?: number;
  Children?: ApiMenuItem[];
  IconName?: string;
  FeatureId?: number;
  IsView?: boolean;
  IsActive?: boolean;
  IsDeleted?: boolean;
  Level?: number;
  ShowInMenu?: boolean;
  HasRouteParam?: boolean;
  LayoutType?: string;
  AccessScope?: string;
  RequiresApproval?: boolean;
  IsMobileVisible?: boolean;
  GroupCode?: string;
}

export interface MenuApiResponse {
  data: ApiMenuItem[];
  success: boolean;
  message: string;
  errors: any;
}

export interface GroupFeatureHierarchyRequest {
  appType: number;
  parentId: number;
  userId: number;
  isMenu: boolean;
  groupId: number;
}

@Injectable({
  providedIn: 'root'
})
export class MenuService {
  private apiUrl = environment.apiUrl;
  private readonly hierarchyCacheKeyPrefix = 'featureHierarchy:';

  constructor(private http: HttpClient) { }

  /**
   * Fetch menu items from Feature Master API (new method)
   */
  getFeatureHierarchyForMenu(appType?: string): Observable<MenuItemType[]> {
    const cacheKey = `${this.hierarchyCacheKeyPrefix}${appType || 'ALL'}`;

    const url = `${this.apiUrl}/Feature/GetHierarchy?onlyAllowed=true${appType !== undefined ? `&appType=${appType}` : ''}`;

    const api$ = this.http.get<MenuApiResponse>(url).pipe(
      map(response => {
        if (response.success && response.data) {
          try {
            localStorage.setItem(cacheKey, JSON.stringify(response.data));
          } catch {
            // ignore localStorage quota/errors
          }
          return this.transformFeatureHierarchyToMenu(response.data);
        }
        throw new Error(response.message || 'Failed to fetch menu items');
      }),
      catchError(error => {
        console.error('❌ Error loading feature hierarchy for menu:', error);
        return of([]);
      })
    );

    // Best-effort: return cached menu immediately if present
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached) as ApiMenuItem[];
        return concat(of(this.transformFeatureHierarchyToMenu(parsed)), api$);
      }
    } catch {
      // ignore cache parsing errors
    }

    return api$;
  }

  /**
   * Fetch menu items from API (legacy UserAccess endpoint)
   */
  getGroupFeatureHierarchy(payload: GroupFeatureHierarchyRequest): Observable<MenuItemType[]> {
    const url = `${this.apiUrl}/UserAccess/group-feature-hierarchy`;
    
    return this.http.post<MenuApiResponse>(url, payload).pipe(
      map(response => {
        if (response.success && response.data) {
          return this.transformApiMenuItems(response.data);
        }
        throw new Error(response.message || 'Failed to fetch menu items');
      }),
      catchError(error => {
        // Return empty array on error or fallback to static menu if needed
        return of([]);
      })
    );
  }

  /**
   * Transform Feature hierarchy to menu items
   */
  private transformFeatureHierarchyToMenu(features: ApiMenuItem[]): MenuItemType[] {
    return features
      .filter(item => 
        (item.isActive !== false && item.IsActive !== false) && // Only active features
        !(item.isDeleted || item.IsDeleted) && // Not deleted
        (item.showInMenu !== false && item.ShowInMenu !== false) // Show in menu
      )
      .map(item => this.mapFeatureToMenuType(item))
      .filter(item => item.isView || !!(item.children && item.children.length > 0));
  }

  /**
   * Map Feature hierarchy item to MenuItemType
   */
  private mapFeatureToMenuType(feature: ApiMenuItem): MenuItemType {
    // Handle both camelCase and PascalCase from backend
    const featureId = feature.featureId || feature.FeatureId || 0;
    const aliasName = feature.aliasName || feature.AliasName || '';
    const featureName = feature.featureName || feature.FeatureName || '';
    const path = feature.path || feature.Path || '#';
    const iconName = feature.iconName || feature.IconName || '';
    const imageIcon = feature.imageIcon || '';
    const isActive = feature.isActive !== false && feature.IsActive !== false;
    const isDeleted = feature.isDeleted || feature.IsDeleted || false;
    const showInMenu = feature.showInMenu !== false && feature.ShowInMenu !== false;
    const rawIsView = feature.isView ?? feature.IsView;
    const isView = rawIsView === true;
    const children = feature.children || feature.Children || [];

    const menuItem: MenuItemType = {
      id: featureId.toString(),
      icon: iconName || this.extractIconClass(imageIcon) || 'list',
      label: (aliasName || featureName) as string,
      route: transformFeaturePathToRoute(path),
      hasChildren: !!(children && children.length > 0),
      expanded: false,
      isView: isView
    };

    // Add children if they exist
    if (menuItem.hasChildren && children) {
      // Filter: active, not deleted, and showInMenu = true
      const activeChildren = children.filter(child => 
        (child.isActive !== false && child.IsActive !== false) &&
        !(child.isDeleted || child.IsDeleted) &&
        (child.showInMenu !== false && child.ShowInMenu !== false)
      );
      
      menuItem.children = activeChildren
        .map(child => this.mapFeatureToMenuType(child))
        .filter(child => child.isView || !!(child.children && child.children.length > 0));
      
      // Recalculate hasChildren after filtering
      menuItem.hasChildren = menuItem.children.length > 0;
    }

    // Parents should stay visible if they have visible children
    menuItem.isView = menuItem.isView || menuItem.hasChildren;

    return menuItem;
  }

  /**
   * Transform API menu items to application menu format
   */
  private transformApiMenuItems(apiItems: ApiMenuItem[]): MenuItemType[] {
    return apiItems.map(item => this.mapApiItemToMenuType(item));
  }

  /**
   * Map single API item to MenuItemType
   */
  private mapApiItemToMenuType(apiItem: ApiMenuItem): MenuItemType {
    const menuItem: MenuItemType = {
      id: apiItem.aliasName || apiItem.routerUrl || apiItem.featureId?.toString() || '0',
      icon: this.extractIconClass(apiItem.imageIcon || ''),
      label: apiItem.aliasName || apiItem.name || 'Unnamed',
      route: transformFeaturePathToRoute(apiItem.path || '#'),
      hasChildren: !!(apiItem.children && apiItem.children.length > 0),
      expanded: apiItem.expanded || false,
      isView: apiItem.isView !== false
    };

    // Add children if they exist
    if (menuItem.hasChildren && apiItem.children) {
      menuItem.children = apiItem.children.map(child => this.mapApiItemToMenuType(child));
    }

    return menuItem;
  }

  /**
   * Transform route path from API to correct Angular route
   */
  // Route path normalization lives in shared utils (feature-route.util.ts)

  /**
   * Extract icon class from imageIcon string
   * Assumes format like "fa fa-list" and extracts the icon name
   */
  private extractIconClass(imageIcon: string): string {
    if (!imageIcon) return 'list';
    
    // Handle Font Awesome icons (e.g., "fa fa-list" -> "list")
    if (imageIcon.includes('fa-')) {
      const parts = imageIcon.split(' ');
      const iconPart = parts.find(part => part.startsWith('fa-'));
      return iconPart ? iconPart.replace('fa-', '') : 'list';
    }
    
    // Handle Material icons or other formats
    return imageIcon.toLowerCase().replace(/[^a-z0-9]/g, '_') || 'list';
  }

  /**
   * Flatten JSON method as requested
   */
  flattenJson(data: any[]): any[] {
    let result: any[] = [];

    const recursiveFlatten = (items: any[]) => {
      for (let item of items) {
        // Copy the item without children
        const { children, ...rest } = item;
        result.push(rest);

        // Recursively process children
        if (children && Array.isArray(children)) {
          recursiveFlatten(children);
        }
      }
    };

    recursiveFlatten(data);
    return result;
  }

  /**
   * For testing/debugging: Log the transformation process
   */
  logMenuTransformation(apiItems: ApiMenuItem[]): void {
    const transformed = this.transformApiMenuItems(apiItems);
    const flattened = this.flattenJson(transformed);
  }

  /**
   * Helper method to get menu items with local data (for testing)
   */
  getMenuItemsWithLocalData(): Observable<MenuItemType[]> {
    // Mock the API response structure for testing
    const mockApiResponse: MenuApiResponse = {
      data: [
        {
          featureName: "Dashboard",
          aliasName: "dashboard",
          path: "/dashboard",
          appType: 'MOBILE',
          parentId: null,
          displayOrder: 1,
          groupId: 1,
          children: [],
          imageIcon: "fa fa-dashboard",
          routerUrl: "dashboard",
          expanded: false,
          featureId: 1,
          name: "Dashboard",
          isView: true
        }
        // Add more items as needed for testing
      ],
      success: true,
      message: "Test data",
      errors: null
    };

    return of(this.transformApiMenuItems(mockApiResponse.data));
  }
}
